import {AppConfig, BaseApp, BaseAppProps, BaseAppState} from "shared/BaseApp";
import React, {ReactElement} from 'react';
import {Main} from "./Main";
import {PathComponent} from "../index";
import {FIREBASE_CONFIG} from "../consts";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {Account, AccountProfile, AppPrefListener, ProvisionedAppConfig} from "../shared/types";
import {AppPrefs} from "./app_prefs";
import {BaseAppPrefs} from "../shared/BaseAppPrefs";
import {LoginConfig} from "../shared/Login";
import {THEME} from "./theme";
import {User} from "@firebase/auth";
import {
  AccountProfileContextTrait,
  AccountTypeName,
  PageClaims,
  Pages,
  PageTypeName,
  PracticeData
} from "@/libwyze-shared/types";
import {getMemberAuth} from "../shared/auth";
import {LibwyzeCommonInit} from "@/libwyze-shared/init";
import Draggable from "react-draggable";
import {mediumGray} from "../shared/colors";
import {BORDER_RADIUS} from "../shared/dimens";
import {Box, Button} from "@mui/material";
import Keyboard from "react-simple-keyboard";
import {CloseOutlined, MoreHorizOutlined} from "@mui/icons-material";

export type AppProps = BaseAppProps & {}

type AppState = BaseAppState & {}

export class App extends ProvisionedApp<AppProps, AppState> implements AccountProfileContextTrait, AppPrefListener {

  protected getLoginConfig(): LoginConfig | null | undefined {
    return {
      provisioning: {
        membersUseSharedProvisioningContext: true,
      },
      uiProps: {
        bottomDecorator: <img src={"/images/dentwyze_checkin_logotype.svg"} style={{height: 48}}/>,
      },
    };
  }

  protected renderSoftKeyboard(): React.ReactElement {
    return <Draggable>
      <div style={{
        zIndex: 1200,
        position: "absolute",
        display: "flex",
        flexDirection: "column",
        left: 16,
        right: 16,
        bottom: 16,
        padding: 8,
        gap: 8,
        background: mediumGray,
        borderRadius: BORDER_RADIUS
      }}>
        <Box style={{display: "flex"}}>
          <MoreHorizOutlined/>
          <span style={{flexGrow: 1}}/>
          <Button variant="text" onClick={() => BaseApp.CONTEXT.hideSoftKeyboard()}>
            <CloseOutlined/>
          </Button>
        </Box>
        <Keyboard/>
      </div>
    </Draggable>
  }

  private account: Account;
  private accountProfile: AccountProfile;

  protected async onAppInitWithLoggedInUser(user: User): Promise<void> {
    await super.onAppInitWithLoggedInUser(user);
    await LibwyzeCommonInit.init(user);

    const memberAuth = getMemberAuth();
    this.account = await memberAuth.getOrLoadAccountByType(AccountTypeName.PROVIDER);
    this.accountProfile = await this.account?.getOrLoadProfile();
    if (this.accountProfile?.type === AccountTypeName.PROVIDER) {
      const claims = await PageClaims.getInstance().loadPageClaimsByAccountId(this.account.id);
      for (const claim of claims) {
        await Pages.getInstance().getOrLoadItem(claim.id);
      }
      const pageId = this.getProviderPageId();
      if (pageId) {
        await Pages.getInstance().getOrLoadItem(pageId);
      }
      const practicePageId = this.getPracticePageId();
      if (practicePageId) {
        const page = await Pages.getInstance().getOrLoadItem(practicePageId);
        // if (page) {
        //   await Smilebook.loadInstance(practicePageId);
        // }
        await PracticeData.createAndInitProvider(practicePageId);
      }
    }
  }

  getAccountProfile<T extends AccountProfile>(): T {
    return this.accountProfile as T;
  }

  getPracticePageId(): string | undefined {
    const selectedPracticeId = AppPrefs.getInstance().getSelectedPracticeId();
    const claims = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
      .filter(claim => claim.pageType === PageTypeName.PRACTICE);
    let pageId = claims.find(claim => claim.id === selectedPracticeId)?.id;
    if (!pageId && claims.length > 0) {
      pageId = claims[0].pageId;
      AppPrefs.getInstance().setSelectedPracticeId(pageId);
    }
    return pageId;
  }

  getProviderPageId(): string | undefined {
    switch (this.accountProfile.type) {
      case AccountTypeName.PROVIDER: {
        const claim = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
          .find(claim => claim.pageType === PageTypeName.PROVIDER);
        return claim?.pageId;
      }
    }
    return undefined;
  }

  getPatientLinkedPracticePageIds(): string[] {
    const accountProfile = this.getAccountProfile();
    switch (accountProfile.type) {
      case AccountTypeName.PROVIDER: {
        const claims = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
          .filter(claim => claim.pageType === PageTypeName.PRACTICE);
        return claims.map(claim => claim.pageId);
      }
    }
    return [];
  }

  getProviderClaimedPracticePageIds(): string[] {
    return [];
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: "Checkin",
      icon: "/logo192.png",
      logo: "/logo192.png",
      stamp: "/images/dentwyze_logotype.svg",
      stampHeight: 40,
      defaultUserImage: "/images/default_user.png",
      theme: THEME,
      privacyUrl: "",
      termsUrl: "",
      styleConfig: {
        defaultContainerSize: "xxxlg",
      },
      loginConfig: this.getLoginConfig(),
      firebaseConfig: {options: FIREBASE_CONFIG},
    };
  }

  getProvisionedAppId(): string {
    return "dentwyze";
  }

  getProvisionedAppConfig(): ProvisionedAppConfig | null {
    return undefined;
  }

  static nestedPaths(): PathComponent[] {
    return Main.nestedPaths();
  }

  getAppPrefs<T extends BaseAppPrefs>(): T {
    //@ts-ignore
    return AppPrefs.getInstance() as T;
  }

  protected renderMain(): ReactElement {
    // if (!Smilebook.getInstance()) {
    //   return <StyledEmpty emptyConfig={{
    //     iconUrl: "/images/error.svg",
    //     title: "There was an error loading your Dentwyze profile",
    //     text: "You must set up your Dentwyze profile and practice in the myCare app before continuing.",
    //     action: new Action("Set up myCare profile", () => {
    //       window.location.href = "https://dentwyze.in/mycare";
    //     }),
    //   }}/>;
    // }
    return <Main path={this.props.path}/>;
  }
}
